/* eslint-disable react/jsx-props-no-spreading */
import { FC as ReactFC, useEffect, useState } from 'react';

import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import OtpInput from 'react-otp-input';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import ImageComponent from 'shared/components/image/ImageComponent';
import Status from 'shared/enums/Status';

import styles from './mfaGaSetupModal.module.scss';
import MfaGaSetupModalProps from './MfaGaSetupModalProps';

const INITIAL_STEP = 1;
const INITIAL_OTP = '';

const MfaGaSetupModal: ReactFC<MfaGaSetupModalProps> = (
  props: MfaGaSetupModalProps
) => {
  const { qr, isOpen, status, onToggle, onFinishSetup } = props;

  const [step, setStep] = useState<number>(INITIAL_STEP);
  const [otp, setOtp] = useState<string>(INITIAL_OTP);

  useEffect(() => {
    if (isOpen) {
      setStep(INITIAL_STEP);
      setOtp(INITIAL_OTP);
    }
  }, [isOpen]);

  const onClosed = (): void => {
    setStep(INITIAL_STEP);
    setOtp(INITIAL_OTP);
  };

  const handlePrimaryAction = (): void => {
    if (step === 1) {
      setStep(2);
    } else {
      onFinishSetup(otp);
    }
  };

  const handleSecondaryAction = (): void => {
    if (step === 2) {
      setStep(1);
      setOtp(INITIAL_OTP);
    } else {
      onToggle();
    }
  };

  const renderStepOne = (): JSX.Element => (
    <Col xs="12">
      <Row className="mb-3">
        <span className="text-14-light">
          {intl.get('LBL_MFA_METHODS_GA_SETUP_MODAL_STEP_01_DESCRIPTION')}
        </span>
      </Row>
      {qr ? (
        <Row className="d-flex flex-column align-items-center">
          <ImageComponent
            loading="eager"
            src={qr}
            className={`${styles.mfaGaSetupQrImg}`}
            alt="MFA GA Setup QR Code"
          />
        </Row>
      ) : (
        <Row className="d-flex flex-column align-items-center mt-4">
          <span className="text-14-light">
            {intl.get('ERR_MFA_QR_CODE_NOT_AVAILABLE')}
          </span>
        </Row>
      )}
    </Col>
  );

  const renderStepTwo = (): JSX.Element => (
    <Col xs="12">
      <Row className="mb-3">
        <span className="text-14-light">
          {intl.get('LBL_MFA_METHODS_GA_SETUP_MODAL_STEP_02_DESCRIPTION')}
        </span>
      </Row>
      <Row className="d-flex flex-column align-items-center mt-4 text-18-medium">
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          // Set inputType to tel to avoid arrow buttons
          inputType="tel"
          containerStyle={styles.otpContainer}
          inputStyle={styles.otpInput}
          shouldAutoFocus
          renderInput={(inputProps) => <input {...inputProps} />}
        />
      </Row>
    </Col>
  );

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={onToggle}
      backdrop="static"
      centered
      autoFocus={false}
      trapFocus
      keyboard={false}
      id="warningPromptTarget"
      onClosed={onClosed}
    >
      <BlockUi tag="div" blocking={status === Status.Loading}>
        <ModalHeader toggle={onToggle}>
          {intl.get('LBL_MFA_METHODS_GA_SETUP_MODAL_TITLE')}
        </ModalHeader>
        <ModalBody>{step === 1 ? renderStepOne() : renderStepTwo()}</ModalBody>
        <ModalFooter className="p-3">
          <Col xs="12" className="d-flex justify-content-between">
            <Button color="secondary" onClick={handleSecondaryAction}>
              {step === 1 ? intl.get('BTN_CANCEL') : intl.get('BTN_MFA_BACK')}
            </Button>
            <Button color="primary" onClick={handlePrimaryAction}>
              {step === 1
                ? intl.get('BTN_MFA_CONTINUE')
                : intl.get('BTN_MFA_FINISH_SETUP')}
            </Button>
          </Col>
        </ModalFooter>
      </BlockUi>
    </Modal>
  );
};

export default MfaGaSetupModal;
